import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const backendUrl = "https://portal.lsb.go.ke/account/licensed/";

function CurrentRegister() {
  const [surveyors, setSurveyors] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(20); // Number of items to fetch per page

  // Debounce search to allow full name input before fetching
  const searchTimeout = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    // Debounce: wait 500ms after user stops typing before updating debouncedSearch
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setCurrentPage(1); // Reset to first page on new search
    }, 500);
    return () => clearTimeout(searchTimeout.current);
    // eslint-disable-next-line
  }, [searchTerm]);

  // Fetch data from the backend with limit, offset, and search term
  const fetchData = async (page, searchValue) => {
    setLoading(true);
    const offset = (page - 1) * itemsPerPage; // Calculate offset based on the current page
    try {
      const response = await fetch(
        `${backendUrl}?limit=${itemsPerPage}&offset=${offset}&search=${encodeURIComponent(searchValue)}`
      );
      const data = await response.json();
      if (data.results.length === 0) {
        setSurveyors([]); // Clear surveyors if user does not exist
        setError("User does not exist");
      } else {
        setSurveyors(data.results);
        setTotalPages(Math.ceil(data.count / itemsPerPage)); // Update total pages based on the total count
        setError(null); // Clear error if data is found
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Does not Exist");
      setLoading(false);
    }
  };

  // Fetch data when component mounts or when the currentPage or debouncedSearch changes
  useEffect(() => {
    fetchData(currentPage, debouncedSearch);
    // eslint-disable-next-line
  }, [currentPage, debouncedSearch]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const CurrentYear = () => {
    const year = new Date().getFullYear();
    return <div style={{
      fontSize: 'calc(.5em + .4vw)'
    }}>{year} STATUS</div>;
  };

  const renderTableHeaders = () => (
    <thead className="uppercase">
      <tr>
        <th scope="col" className="py-3 px-3 bg-black text-white">
          NAME
        </th>
        <th scope="col" className="py-3 px-3 bg-black text-white">
          LICENCE
        </th>
        <th scope="col" className="py-3 px-3 bg-black text-white">
          ORGANIZATION
        </th>
        <th scope="col" className="py-3 px-3 bg-black text-white">
          ADDRESS
        </th>
        <th scope="col" className="py-3 px-3 bg-black text-white">
          <CurrentYear />
        </th>

      </tr>
    </thead>
  );

  const renderTableRows = (data) => {
    if (data.length === 0) {
      return <tr><td colSpan="6" className="text-center">User does not exist</td></tr>;
    }
    return (
      <tbody>
        {data.map((surveyor, index) => (
          <tr
            key={index}
            className="bg-white border-b border-gray-100 hover:bg-gray-50"
          >
            <td className="px-3 py-2.5">
              <Link
                to={`/profile/${surveyor.license_no}`}
                className="text-[#3d0707] text-decoration-none hover:font-medium"

              >
                {surveyor.name}
              </Link>
            </td>
            <td className="px-3 py-2.5">{surveyor.license_no}</td>
            <td className="px-3 py-2.5">{surveyor.firm}</td>
            <td className="px-3 py-2.5">{surveyor.postal_address}</td>
            <td className="px-3 py-2.5">{surveyor.status}</td>
          </tr>
        ))}
      </tbody>
    );
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return (
      <div>
        <div className="relative overflow-x-auto">
          <label
            htmlFor="licensedSurveyor"
            className="relative block overflow-hidden border-b border-gray-200 bg-transparent pt-3 focus-within:border-blue-600"
          >
            <input
              type="text"
              placeholder="Search by name..."
              className="peer h-12 w-full border-none bg-gray-200 px-4 placeholder-transparent focus:border-transparent focus:outline-none focus:ring-0 rounded-t-lg fw-bold"
              value={searchTerm}
              onChange={handleSearchChange}
              autoFocus
            />

            <span
              className="absolute start-0 top-2 -translate-y-1/2 text-xs text-gray-800 transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:text-xs px-4"
            >
              Search by name...
            </span>
          </label>
        </div>
        <div>Error: {error}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="relative overflow-x-auto">
        <label
          htmlFor="licensedSurveyor"
          className="relative block overflow-hidden border-b border-gray-200 bg-transparent pt-3 focus-within:border-blue-600"
        >
          <input
            type="text"
            placeholder=""
            className="peer h-12 w-full border-none bg-gray-200 px-4 placeholder-transparent focus:border-transparent focus:outline-none focus:ring-0 rounded-t-lg"
            value={searchTerm}
            onChange={handleSearchChange}
            autoFocus
          />

          <span
            className="absolute start-0 top-2 -translate-y-1/2 text-xs text-gray-800 transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-sm peer-focus:top-2 peer-focus:text-xs px-4"
          >
            Search by name...
          </span>
        </label>
      </div>

      <div className="overflow-x-auto mt-2">
        <table className="w-full text-sm text-left rtl:text-right table">
          {renderTableHeaders()}
          {renderTableRows(surveyors)}
        </table>
      </div>

      <span style={{ fontSize: 'calc(.7em + .3vw)' }} className="italic font-medium">Page {currentPage} of {totalPages}</span>

      <div className="flex justify-start items-center gap-2 pt-2">
        <button className="button mt-4" onClick={handlePreviousPage}
          disabled={currentPage === 1}>
          <span className="button_lg">
            <span className="button_sl"></span>
            <span className="button_text" style={{ fontSize: 'calc(.5em + .3vw)' }}>previous</span>
          </span>
        </button>

        <button className="button mt-4" onClick={handleNextPage}
          disabled={currentPage === totalPages}>
          <span className="button_lg">
            <span className="button_sl"></span>
            <span className="button_text" style={{ fontSize: 'calc(.5em + .3vw)' }}>next</span>
          </span>
        </button>
      </div>
    </div>
  );
}

export default CurrentRegister;